/**
 * Smart Registry Operations Service
 *
 * Provides intelligent registry management including 3D visualization,
 * optimal file placement, space utilization, and IoT/RFID integration.
 */

export interface RegistryLayout {
  id: string;
  name: string;
  mda: string;
  building: string;
  floor: number;
  room: string;
  dimensions: {
    width: number;  // meters
    length: number; // meters
    height: number; // meters
  };
  cabinets: Cabinet[];
  aisles: Aisle[];
  workstations: Workstation[];
  environmentalSensors: EnvironmentalSensor[];
  lastUpdated: Date;
}

export interface Cabinet {
  id: string;
  name: string;
  type: CabinetType;
  position: Position3D;
  dimensions: {
    width: number;
    depth: number;
    height: number;
  };
  shelves: Shelf[];
  status: 'available' | 'full' | 'maintenance' | 'reserved';
  rfidReaderId?: string;
  totalCapacity: number;
  usedCapacity: number;
  utilizationPercentage: number;
}

export type CabinetType =
  | 'standard_filing'   // Traditional filing cabinet
  | 'lateral_filing'    // Wide lateral cabinet
  | 'mobile_shelving'   // Compactable mobile shelves
  | 'fire_resistant'    // Fireproof cabinet
  | 'high_security';    // High-security cabinet

export interface Shelf {
  id: string;
  cabinetId: string;
  level: number;  // 1 = bottom
  label: string;
  capacity: number;
  usedSlots: number;
  files: ShelfFile[];
  rangeStart?: string;  // File number range start
  rangeEnd?: string;    // File number range end
}

export interface ShelfFile {
  pfileId: string;
  fileNumber: string;
  staffName: string;
  position: number;  // Position on shelf
  rfidTag?: string;
  lastScanned?: Date;
  thickness: number; // cm
}

export interface Position3D {
  x: number;
  y: number;
  z: number;
}

export interface Aisle {
  id: string;
  name: string;
  startPosition: Position3D;
  endPosition: Position3D;
  width: number;
  isAccessible: boolean;
}

export interface Workstation {
  id: string;
  name: string;
  type: 'scanning' | 'processing' | 'checkout' | 'reception';
  position: Position3D;
  operatorId?: string;
  status: 'available' | 'occupied' | 'offline';
}

export interface EnvironmentalSensor {
  id: string;
  type: 'temperature' | 'humidity' | 'smoke' | 'water_leak';
  position: Position3D;
  status: 'normal' | 'warning' | 'critical' | 'offline';
  currentReading?: number;
  unit?: string;
  thresholds: {
    warning: number;
    critical: number;
  };
  lastReading: Date;
}

export interface RFIDReading {
  tagId: string;
  readerId: string;
  timestamp: Date;
  signalStrength: number;
  pfileId?: string;
  fileNumber?: string;
}

export interface FileLocationResult {
  pfileId: string;
  fileNumber: string;
  staffName: string;
  location: {
    building: string;
    room: string;
    cabinet: string;
    shelf: string;
    position: number;
  };
  coordinates: Position3D;
  navigationPath?: NavigationStep[];
  estimatedRetrievalTime: number; // seconds
  isAvailable: boolean;
  lastVerified: Date;
}

export interface NavigationStep {
  instruction: string;
  direction: 'forward' | 'left' | 'right' | 'up' | 'down';
  distance?: number; // meters
  landmark?: string;
}

export interface SpaceUtilization {
  registryId: string;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  utilizationPercentage: number;
  byCabinet: Array<{
    cabinetId: string;
    name: string;
    utilization: number;
  }>;
  byType: Record<CabinetType, {
    total: number;
    used: number;
    percentage: number;
  }>;
  projectedFullDate?: Date;
  recommendations: string[];
}

export interface PlacementSuggestion {
  fileNumber: string;
  suggestedLocation: {
    cabinet: string;
    shelf: string;
    position: number;
  };
  reason: string;
  alternatives: Array<{
    cabinet: string;
    shelf: string;
    score: number;
  }>;
}

export interface EnvironmentalAlert {
  id: string;
  sensorId: string;
  type: 'temperature' | 'humidity' | 'smoke' | 'water_leak';
  severity: 'warning' | 'critical';
  message: string;
  reading: number;
  threshold: number;
  timestamp: Date;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  resolution?: string;
}

class SmartRegistryService {
  /**
   * Create a registry layout
   */
  createRegistryLayout(params: {
    name: string;
    mda: string;
    building: string;
    floor: number;
    room: string;
    dimensions: { width: number; length: number; height: number };
  }): RegistryLayout {
    return {
      id: `registry_${Date.now()}`,
      name: params.name,
      mda: params.mda,
      building: params.building,
      floor: params.floor,
      room: params.room,
      dimensions: params.dimensions,
      cabinets: [],
      aisles: [],
      workstations: [],
      environmentalSensors: [],
      lastUpdated: new Date(),
    };
  }

  /**
   * Add a cabinet to the registry
   */
  addCabinet(
    _registry: RegistryLayout,
    params: {
      name: string;
      type: CabinetType;
      position: Position3D;
      dimensions: { width: number; depth: number; height: number };
      shelfCount: number;
      capacityPerShelf: number;
    }
  ): Cabinet {
    const shelves: Shelf[] = [];
    for (let i = 1; i <= params.shelfCount; i++) {
      shelves.push({
        id: `shelf_${Date.now()}_${i}`,
        cabinetId: '',
        level: i,
        label: `Shelf ${i}`,
        capacity: params.capacityPerShelf,
        usedSlots: 0,
        files: [],
      });
    }

    const cabinet: Cabinet = {
      id: `cabinet_${Date.now()}`,
      name: params.name,
      type: params.type,
      position: params.position,
      dimensions: params.dimensions,
      shelves: shelves.map(s => ({ ...s, cabinetId: `cabinet_${Date.now()}` })),
      status: 'available',
      totalCapacity: params.shelfCount * params.capacityPerShelf,
      usedCapacity: 0,
      utilizationPercentage: 0,
    };

    return cabinet;
  }

  /**
   * Find file location in registry
   */
  findFileLocation(
    registry: RegistryLayout,
    fileNumber: string
  ): FileLocationResult | null {
    for (const cabinet of registry.cabinets) {
      for (const shelf of cabinet.shelves) {
        const file = shelf.files.find(f => f.fileNumber === fileNumber);
        if (file) {
          return {
            pfileId: file.pfileId,
            fileNumber: file.fileNumber,
            staffName: file.staffName,
            location: {
              building: registry.building,
              room: registry.room,
              cabinet: cabinet.name,
              shelf: shelf.label,
              position: file.position,
            },
            coordinates: {
              x: cabinet.position.x,
              y: cabinet.position.y,
              z: cabinet.position.z + (shelf.level * 0.3), // Approximate shelf height
            },
            navigationPath: this.generateNavigationPath(registry, cabinet, shelf),
            estimatedRetrievalTime: this.estimateRetrievalTime(cabinet, shelf),
            isAvailable: true,
            lastVerified: file.lastScanned || new Date(),
          };
        }
      }
    }
    return null;
  }

  /**
   * Generate navigation path to file location
   */
  private generateNavigationPath(
    registry: RegistryLayout,
    cabinet: Cabinet,
    shelf: Shelf
  ): NavigationStep[] {
    const steps: NavigationStep[] = [];

    // Entry instruction
    steps.push({
      instruction: `Enter ${registry.room} on floor ${registry.floor}`,
      direction: 'forward',
      landmark: 'Main entrance',
    });

    // Navigate to cabinet
    steps.push({
      instruction: `Walk to ${cabinet.name}`,
      direction: 'forward',
      distance: Math.sqrt(cabinet.position.x ** 2 + cabinet.position.y ** 2),
      landmark: cabinet.name,
    });

    // Shelf instruction
    if (shelf.level <= 2) {
      steps.push({
        instruction: `Look at ${shelf.label} (lower section)`,
        direction: 'down',
      });
    } else if (shelf.level >= 5) {
      steps.push({
        instruction: `Look at ${shelf.label} (upper section - use step stool if needed)`,
        direction: 'up',
      });
    } else {
      steps.push({
        instruction: `Look at ${shelf.label} (eye level)`,
        direction: 'forward',
      });
    }

    return steps;
  }

  /**
   * Estimate file retrieval time
   */
  private estimateRetrievalTime(cabinet: Cabinet, shelf: Shelf): number {
    let seconds = 30; // Base time

    // Add time based on distance (simplified)
    seconds += Math.sqrt(cabinet.position.x ** 2 + cabinet.position.y ** 2) * 2;

    // Add time for high/low shelves
    if (shelf.level <= 2 || shelf.level >= 5) {
      seconds += 15;
    }

    // Add time for special cabinet types
    if (cabinet.type === 'high_security') {
      seconds += 30; // Access control
    } else if (cabinet.type === 'mobile_shelving') {
      seconds += 20; // Moving shelves
    }

    return Math.round(seconds);
  }

  /**
   * Suggest optimal placement for new file
   */
  suggestPlacement(
    registry: RegistryLayout,
    fileNumber: string,
    _staffName: string
  ): PlacementSuggestion {
    const suggestions: Array<{
      cabinet: Cabinet;
      shelf: Shelf;
      score: number;
      reason: string;
    }> = [];

    for (const cabinet of registry.cabinets) {
      if (cabinet.status !== 'available' || cabinet.utilizationPercentage >= 95) {
        continue;
      }

      for (const shelf of cabinet.shelves) {
        if (shelf.usedSlots >= shelf.capacity) {
          continue;
        }

        let score = 100;
        let reason = '';

        // Check file number range
        if (shelf.rangeStart && shelf.rangeEnd) {
          if (fileNumber >= shelf.rangeStart && fileNumber <= shelf.rangeEnd) {
            score += 50;
            reason = 'Matches file number range';
          }
        }

        // Prefer less utilized shelves
        const utilizationPenalty = (shelf.usedSlots / shelf.capacity) * 30;
        score -= utilizationPenalty;

        // Prefer middle shelves (easier access)
        if (shelf.level >= 2 && shelf.level <= 4) {
          score += 10;
          if (!reason) reason = 'Easy access shelf';
        }

        suggestions.push({ cabinet, shelf, score, reason: reason || 'Available space' });
      }
    }

    // Sort by score
    suggestions.sort((a, b) => b.score - a.score);

    const best = suggestions[0];
    const alternatives = suggestions.slice(1, 4).map(s => ({
      cabinet: s.cabinet.name,
      shelf: s.shelf.label,
      score: s.score,
    }));

    return {
      fileNumber,
      suggestedLocation: {
        cabinet: best?.cabinet.name || 'No space available',
        shelf: best?.shelf.label || '',
        position: best?.shelf.usedSlots + 1 || 0,
      },
      reason: best?.reason || 'Registry full',
      alternatives,
    };
  }

  /**
   * Calculate space utilization
   */
  calculateSpaceUtilization(registry: RegistryLayout): SpaceUtilization {
    let totalCapacity = 0;
    let usedCapacity = 0;

    const byCabinet: Array<{ cabinetId: string; name: string; utilization: number }> = [];
    const byType: Record<CabinetType, { total: number; used: number; percentage: number }> = {
      standard_filing: { total: 0, used: 0, percentage: 0 },
      lateral_filing: { total: 0, used: 0, percentage: 0 },
      mobile_shelving: { total: 0, used: 0, percentage: 0 },
      fire_resistant: { total: 0, used: 0, percentage: 0 },
      high_security: { total: 0, used: 0, percentage: 0 },
    };

    for (const cabinet of registry.cabinets) {
      totalCapacity += cabinet.totalCapacity;
      usedCapacity += cabinet.usedCapacity;

      byCabinet.push({
        cabinetId: cabinet.id,
        name: cabinet.name,
        utilization: cabinet.utilizationPercentage,
      });

      byType[cabinet.type].total += cabinet.totalCapacity;
      byType[cabinet.type].used += cabinet.usedCapacity;
    }

    // Calculate percentages
    for (const type of Object.keys(byType) as CabinetType[]) {
      if (byType[type].total > 0) {
        byType[type].percentage = Math.round((byType[type].used / byType[type].total) * 100);
      }
    }

    const utilizationPercentage = totalCapacity > 0
      ? Math.round((usedCapacity / totalCapacity) * 100)
      : 0;

    // Generate recommendations
    const recommendations: string[] = [];
    if (utilizationPercentage >= 90) {
      recommendations.push('Critical: Registry approaching full capacity. Consider expansion or archival.');
    } else if (utilizationPercentage >= 75) {
      recommendations.push('Warning: Registry utilization high. Plan for additional storage.');
    }

    // Find underutilized cabinets
    const underutilized = byCabinet.filter(c => c.utilization < 30);
    if (underutilized.length > 0) {
      recommendations.push(`Consolidation opportunity: ${underutilized.length} cabinets under 30% utilized.`);
    }

    // Projected full date (simplified linear projection)
    const monthlyGrowthRate = 0.02; // 2% monthly growth estimate
    const remainingCapacity = totalCapacity - usedCapacity;
    const monthsUntilFull = remainingCapacity / (usedCapacity * monthlyGrowthRate);
    const projectedFullDate = new Date();
    projectedFullDate.setMonth(projectedFullDate.getMonth() + Math.round(monthsUntilFull));

    return {
      registryId: registry.id,
      totalCapacity,
      usedCapacity,
      availableCapacity: totalCapacity - usedCapacity,
      utilizationPercentage,
      byCabinet,
      byType,
      projectedFullDate: utilizationPercentage < 100 ? projectedFullDate : undefined,
      recommendations,
    };
  }

  /**
   * Process RFID reading
   */
  processRFIDReading(reading: RFIDReading): {
    status: 'identified' | 'unidentified' | 'unauthorized_movement';
    file?: ShelfFile;
    alert?: EnvironmentalAlert;
  } {
    // This would integrate with actual RFID system
    // For now, return a simulated response
    return {
      status: 'identified',
      file: reading.pfileId ? {
        pfileId: reading.pfileId,
        fileNumber: reading.fileNumber || '',
        staffName: '',
        position: 0,
        rfidTag: reading.tagId,
        lastScanned: reading.timestamp,
        thickness: 3,
      } : undefined,
    };
  }

  /**
   * Check environmental conditions
   */
  checkEnvironmentalConditions(sensors: EnvironmentalSensor[]): EnvironmentalAlert[] {
    const alerts: EnvironmentalAlert[] = [];

    for (const sensor of sensors) {
      if (sensor.status === 'offline') continue;
      if (sensor.currentReading === undefined) continue;

      if (sensor.currentReading >= sensor.thresholds.critical) {
        alerts.push({
          id: `alert_${sensor.id}_${Date.now()}`,
          sensorId: sensor.id,
          type: sensor.type,
          severity: 'critical',
          message: `${sensor.type.replace('_', ' ')} critical: ${sensor.currentReading}${sensor.unit}`,
          reading: sensor.currentReading,
          threshold: sensor.thresholds.critical,
          timestamp: new Date(),
          acknowledged: false,
        });
      } else if (sensor.currentReading >= sensor.thresholds.warning) {
        alerts.push({
          id: `alert_${sensor.id}_${Date.now()}`,
          sensorId: sensor.id,
          type: sensor.type,
          severity: 'warning',
          message: `${sensor.type.replace('_', ' ')} warning: ${sensor.currentReading}${sensor.unit}`,
          reading: sensor.currentReading,
          threshold: sensor.thresholds.warning,
          timestamp: new Date(),
          acknowledged: false,
        });
      }
    }

    return alerts;
  }

  /**
   * Get optimal environmental conditions for records storage
   */
  getOptimalConditions(): {
    temperature: { min: number; max: number; unit: string };
    humidity: { min: number; max: number; unit: string };
  } {
    return {
      temperature: { min: 18, max: 22, unit: '°C' },
      humidity: { min: 35, max: 55, unit: '%RH' },
    };
  }
}

// Export singleton instance
export const smartRegistry = new SmartRegistryService();

export type { SmartRegistryService };
